import type { MockAITemplateType } from "../utils/generateExcalidrawTemplate";
import type { DrawLibraryLightCatalogItem } from "./drawLibraryService";

const DRAW_API_URL = import.meta.env.VITE_DRAW_API_URL;
const GENERATE_BOARD_TIMEOUT_MS = 90000;

export type AIBoardGenerationErrorReason =
  | "missing_config"
  | "fetch_failed"
  | "timeout"
  | "http_error"
  | "invalid_json"
  | "api_error"
  | "missing_elements";

export class AIBoardGenerationError extends Error {
  reason: AIBoardGenerationErrorReason;
  status?: number;
  responseBody?: string;

  constructor({
    message,
    reason,
    status,
    responseBody,
  }: {
    message: string;
    reason: AIBoardGenerationErrorReason;
    status?: number;
    responseBody?: string;
  }) {
    super(message);
    this.name = "AIBoardGenerationError";
    this.reason = reason;
    this.status = status;
    this.responseBody = responseBody;
  }
}

export type AIBoardGenerationPayloadType =
  | "presentation"
  | "sales_funnel"
  | "process"
  | "mind_map"
  | "customer_journey"
  | "crm";

export type GenerateBoardWithAIParams = {
  prompt: string;
  generationType: MockAITemplateType;
  userId: string;
  currentBoardId?: string | null;
  useLibrary?: boolean;
  libraryCategory?: string;
  libraryCatalog?: DrawLibraryLightCatalogItem[];
};

export type GenerateBoardWithAIResponse = {
  success: boolean;
  data?: {
    title?: string;
    elements?: unknown[];
    appState?: unknown;
    files?: Record<string, unknown>;
  };
  model?: string;
  metadata?: Record<string, unknown>;
};

const generationTypeForAPI: Record<
  MockAITemplateType,
  AIBoardGenerationPayloadType
> = {
  presentation: "presentation",
  "sales-funnel": "sales_funnel",
  process: "process",
  "mind-map": "mind_map",
  "customer-journey": "customer_journey",
  crm: "crm",
};

export const toAIBoardGenerationPayloadType = (
  generationType: MockAITemplateType,
) => generationTypeForAPI[generationType];

const getDrawAPIUrl = () => {
  if (!DRAW_API_URL) {
    throw new AIBoardGenerationError({
      reason: "missing_config",
      message: "VITE_DRAW_API_URL nao configurada.",
    });
  }

  return DRAW_API_URL.replace(/\/$/, "");
};

const logGenerationFailure = (error: unknown) => {
  if (error instanceof AIBoardGenerationError) {
    console.error("[AI Board API] generation failed", {
      reason: error.reason,
      status: error.status,
      responseBody: error.responseBody,
      message: error.message,
    });
    return;
  }

  console.error("[AI Board API] generation failed", error);
};

export const generateBoardWithAI = async ({
  prompt,
  generationType,
  userId,
  currentBoardId,
  useLibrary = false,
  libraryCategory,
  libraryCatalog,
}: GenerateBoardWithAIParams): Promise<GenerateBoardWithAIResponse> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, GENERATE_BOARD_TIMEOUT_MS);
  const url = `${getDrawAPIUrl()}/ai/generate-board`;

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        prompt,
        generationType: toAIBoardGenerationPayloadType(generationType),
        currentBoardId: currentBoardId ?? null,
        userId,
        format: "excalidraw_elements",
        ...(useLibrary
          ? {
              useLibrary: true,
              libraryCategory: libraryCategory || "automatic",
              libraryCatalog: libraryCatalog || [],
            }
          : {}),
      }),
      signal: controller.signal,
    });
    const responseText = await response.text();

    if (!response.ok) {
      console.error("[AI Board API] error body", responseText);
      throw new AIBoardGenerationError({
        reason: "http_error",
        status: response.status,
        responseBody: responseText,
        message: `HTTP ${response.status} ao chamar API de IA.`,
      });
    }

    let result: GenerateBoardWithAIResponse;

    try {
      result = JSON.parse(responseText) as GenerateBoardWithAIResponse;
    } catch (error) {
      console.error("[AI Board API] invalid JSON body", responseText);
      throw new AIBoardGenerationError({
        reason: "invalid_json",
        responseBody: responseText,
        message: "A API retornou JSON invalido.",
      });
    }

    if (!result?.success) {
      throw new AIBoardGenerationError({
        reason: "api_error",
        status: response.status,
        responseBody: responseText,
        message: "A API retornou success=false.",
      });
    }

    if (!Array.isArray(result.data?.elements) || !result.data.elements.length) {
      throw new AIBoardGenerationError({
        reason: "missing_elements",
        status: response.status,
        responseBody: responseText,
        message: "A API retornou resposta sem elements.",
      });
    }

    return result;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      const timeoutError = new AIBoardGenerationError({
        reason: "timeout",
        message: "A geracao com IA real demorou demais.",
      });
      logGenerationFailure(timeoutError);
      throw timeoutError;
    }

    if (error instanceof AIBoardGenerationError) {
      logGenerationFailure(error);
      throw error;
    }

    const fetchError = new AIBoardGenerationError({
      reason: "fetch_failed",
      message:
        "API indisponivel, URL incorreta ou CORS bloqueado pelo navegador.",
    });
    console.error("[AI Board API] fetch failed", {
      url,
      originalMessage: error instanceof Error ? error.message : String(error),
    });
    throw fetchError;
  } finally {
    window.clearTimeout(timeoutId);
  }
};
